'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { getSettings, type SettingsPayload } from '@/lib/cms';
import { normalizeSocialLinks, SocialLinksList } from '@/lib/social-links';
import { LegalFooterLinkList } from '@/components/legal/useLegalFooterLinks';

const productLinks = [
  { label: 'Explore Events', href: '/events' },
  { label: 'Calendar', href: '/events/calendar' },
  { label: 'Host Event', href: '/host-events' },
  { label: 'How It Works', href: '/how-it-works' },
];
const companyLinks = [
  { label: 'About', href: '/about' },
  { label: 'Blog', href: '/blog' },
  { label: 'Careers', href: '/careers' },
  { label: 'Contact', href: '/contact' },
];

export function Footer() {
  const [settings, setSettings] = useState<SettingsPayload | null>(null);

  useEffect(() => {
    getSettings().then(setSettings).catch(() => setSettings({}));
  }, []);

  const footerText = settings?.footer_text ?? 'The modern platform for hosting hackathons, conferences, and large-scale events.';
  const socialLinks = normalizeSocialLinks(settings?.social_links as Record<string, string> | undefined);
  const hasSocialLinks = socialLinks.length > 0;

  return (
    <footer className="relative z-20 py-8 px-4 sm:px-6 lg:px-8 border-t border-nature-border bg-bg/88 backdrop-blur-md rounded-t-2xl">
      <div className="max-w-5xl mx-auto text-center">
        <div className={`grid grid-cols-2 gap-6 mb-6 ${hasSocialLinks ? 'md:grid-cols-5' : 'md:grid-cols-4'}`}>
          <div className="col-span-2 md:col-span-1 flex flex-col items-center md:items-center">
            <Link href="/" className="flex items-center gap-3 mb-2 group">
              <div className="relative h-16 w-40 flex items-center transition-transform duration-300 group-hover:scale-105 px-1">
                <Image
                  src="/logo-eh.png"
                  alt="EnthiosHub"
                  fill
                  className="object-contain"
                />
              </div>
            </Link>
            <p className="text-nature-muted text-xs max-w-[180px]">{footerText}</p>
          </div>
          <div className="flex flex-col items-center">
            <h4 className="font-semibold mb-3 text-sm">Services</h4>
            <ul className="space-y-1.5 text-nature-muted text-sm">
              {productLinks.map((item) => (
                <li key={item.href}>
                  <Link href={item.href} className="hover:text-nature-text transition-colors">{item.label}</Link>
                </li>
              ))}
            </ul>
          </div>
          <div className="flex flex-col items-center">
            <h4 className="font-semibold mb-3 text-sm">Company</h4>
            <ul className="space-y-1.5 text-nature-muted text-sm">
              {companyLinks.map((item) => (
                <li key={item.href}>
                  <Link href={item.href} className="hover:text-nature-text transition-colors">{item.label}</Link>
                </li>
              ))}
            </ul>
          </div>
          <div className="flex flex-col items-center">
            <h4 className="font-semibold mb-3 text-sm">Legal</h4>
            <LegalFooterLinkList />
          </div>
          {hasSocialLinks && (
            <div className="flex flex-col items-center col-span-2 md:col-span-1">
              <h4 className="font-semibold mb-3 text-sm">Social</h4>
              <SocialLinksList links={socialLinks} />
            </div>
          )}
        </div>
      </div>
    </footer>
  );
}
